import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  Avatar,
  AvatarGroup,
  IconButton,
  Tooltip,
  Badge,
  Fab,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Skeleton,
  TextField,
  InputAdornment,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Add as AddIcon,
  Group as GroupIcon,
  Campaign as CampaignIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  StarBorder as StarBorderIcon,
  Share as ShareIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Euro as EuroIcon,
  Search as SearchIcon,
  AccountBalance as FundingTypeIcon,
  Article as PublicationTypeIcon,
  Science as ResearchTypeIcon,
  School as TeachingTypeIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import useSWR, { mutate as globalMutate } from 'swr';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { axiosService } from "shared/helpers/axios";
import { useUser } from "shared/helpers/axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import EnhancedCreateConsortiumDialog from "./EnhancedCreateConsortiumDialog";
import { ROLE_RANK } from 'shared/constants/roleConstants';
import { useTheme } from "@mui/material/styles";
import ShareButton from 'components/common/buttons/ShareButton';
import PageContainer from 'components/common/layout/PageContainer';
import PageHeader, { HEADER_TABS_SX } from 'components/common/layout/PageHeader';

// Utility function to ensure data is an array
const ensureArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data === null || data === undefined) return [];
  if (typeof data === 'object') return Object.values(data);
  return [data];
};

// Fetcher function for SWR
const fetcher = (url) => axiosService.get(url).then(res => {
  // Handle paginated responses by extracting results array
  if (res.data && typeof res.data === 'object' && Array.isArray(res.data.results)) {
    return res.data.results;
  }
  // For non-paginated responses, return data as-is
  return Array.isArray(res.data) ? res.data : [];
});

// Mock data fallback function
// const getMockConsortiums = () => [
//   {
//     id: "550e8400-e29b-41d4-a716-446655440001",
//     name: "AI for Healthcare Consortium",
//     summary: "Developing AI solutions for early disease detection and personalized treatment plans using advanced machine learning techniques.",
//     status: "active",
//     status_display: "Active",
//     call_title: "Horizon Europe Health Cluster",
//     call_code: "HORIZON-HLTH-2024",
//     coordinator_org_name: "MIT",
//     coordinator_member_name: "Dr. Jane Smith",
//     member_count: 8,
//     total_budget_requested: 2500000,
//     total_budget_own: 500000,
//     duration_months: 24,
//     created: "2024-01-15T00:00:00Z",
//     memberships: [
//       {
//         id: "550e8400-e29b-41d4-a716-446655440010",
//         member: { id: "550e8400-e29b-41d4-a716-446655440011", name: "Dr. Jane Smith" },
//         organization: { id: "550e8400-e29b-41d4-a716-446655440012", name: "MIT" },
//         roles: ["coordinator"]
//       },
//       {
//         id: "550e8400-e29b-41d4-a716-446655440013",
//         member: { id: "550e8400-e29b-41d4-a716-446655440014", name: "Prof. John Doe" },
//         organization: { id: "550e8400-e29b-41d4-a716-446655440015", name: "Harvard Medical School" },
//         roles: ["researcher"]
//       },
//       {
//         id: "550e8400-e29b-41d4-a716-446655440016",
//         member: { id: "550e8400-e29b-41d4-a716-446655440017", name: "Dr. Alice Brown" },
//         organization: { id: "550e8400-e29b-41d4-a716-446655440018", name: "Stanford University" },
//         roles: ["researcher"]
//       }
//     ]
//   },
//   {
//     id: "550e8400-e29b-41d4-a716-446655440002",
//     name: "Sustainable Energy Research Network",
//     summary: "Creating innovative renewable energy solutions for the next generation with focus on solar and wind technologies.",
//     status: "forming",
//     status_display: "Forming",
//     call_title: "Clean Energy Transition",
//     call_code: "HORIZON-CL5-2024",
//     coordinator_org_name: "University of Oxford",
//     coordinator_member_name: "Prof. Maria Garcia",
//     member_count: 3,
//     total_budget_requested: 1800000,
//     total_budget_own: 300000,
//     duration_months: 36,
//     created: "2024-02-01T00:00:00Z",
//     memberships: [
//       {
//         id: "550e8400-e29b-41d4-a716-446655440019",
//         member: { id: "550e8400-e29b-41d4-a716-446655440020", name: "Prof. Maria Garcia" },
//         organization: { id: "550e8400-e29b-41d4-a716-446655440021", name: "University of Oxford" },
//         roles: ["coordinator"]
//       },
//       {
//         id: "550e8400-e29b-41d4-a716-446655440022",
//         member: { id: "550e8400-e29b-41d4-a716-446655440023", name: "Dr. David Lee" },
//         organization: { id: "550e8400-e29b-41d4-a716-446655440024", name: "ETH Zurich" },
//         roles: ["researcher"]
//       }
//     ]
//   },
//   {
//     id: "550e8400-e29b-41d4-a716-446655440003",
//     name: "Digital Innovation Hub",
//     summary: "Advancing digital transformation across European SMEs through collaborative research and technology transfer.",
//     status: "active",
//     status_display: "Active",
//     call_title: "Digital Single Market",
//     call_code: "HORIZON-CL4-2024",
//     coordinator_org_name: "Stanford University",
//     coordinator_member_name: "Dr. Sophie Wilson",
//     member_count: 15,
//     total_budget_requested: 3200000,
//     total_budget_own: 800000,
//     duration_months: 18,
//     created: "2024-01-20T00:00:00Z",
//     memberships: [
//       {
//         id: "550e8400-e29b-41d4-a716-446655440025",
//         member: { id: "550e8400-e29b-41d4-a716-446655440026", name: "Dr. Sophie Wilson" },
//         organization: { id: "550e8400-e29b-41d4-a716-446655440027", name: "Stanford University" },
//         roles: ["coordinator"]
//       },
//       {
//         id: "550e8400-e29b-41d4-a716-446655440028",
//         member: { id: "550e8400-e29b-41d4-a716-446655440029", name: "Mr. Tech Expert" },
//         organization: { id: "550e8400-e29b-41d4-a716-446655440030", name: "Technical University Berlin" },
//         roles: ["researcher"]
//       }
//     ]
//   },
//   {
//     id: "550e8400-e29b-41d4-a716-446655440004",
//     name: "Climate Change Mitigation Alliance",
//     summary: "Collaborative research on climate change mitigation strategies and carbon capture technologies.",
//     status: "forming",
//     status_display: "Forming",
//     call_title: "European Green Deal",
//     call_code: "HORIZON-MISS-2024",
//     coordinator_org_name: "ETH Zurich",
//     coordinator_member_name: "Dr. Climate Expert",
//     member_count: 5,
//     total_budget_requested: 2100000,
//     total_budget_own: 400000,
//     duration_months: 30,
//     created: "2024-02-10T00:00:00Z",
//     memberships: [
//       {
//         id: "550e8400-e29b-41d4-a716-446655440031",
//         member: { id: "550e8400-e29b-41d4-a716-446655440032", name: "Dr. Climate Expert" },
//         organization: { id: "550e8400-e29b-41d4-a716-446655440033", name: "ETH Zurich" },
//         roles: ["coordinator"]
//       }
//     ]
//   }
// ];

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  background: 'linear-gradient(135deg, #f8f9fa2d 0%, #e9ecef17 100%)',
  border: '1px solid rgba(0, 0, 0, 0.07)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
    background: 'linear-gradient(135deg, #f8f9fa40 0%, #e9ecef3c 100%)',
    '& .MuiChip-root': {
      backgroundColor: 'rgba(99, 102, 241, 0.1)',
    },
    '& .MuiButton-root': {
      backgroundColor: 'rgba(99, 102, 241, 0.05)',
      borderColor: 'rgba(99, 102, 241, 0.2)',
    }
  },
}));

const PROPOSAL_STATUS_COLORS = {
  preparing:    { bg: 'rgba(255, 152, 0, 0.15)',    color: '#ef6c00' },
  submitted:    { bg: 'rgba(33, 150, 243, 0.15)',   color: '#1565c0' },
  under_review: { bg: 'rgba(156, 39, 176, 0.12)',   color: '#6a1b9a' },
  shortlisted:  { bg: 'rgba(0, 188, 212, 0.15)',    color: '#00838f' },
  awarded:      { bg: 'rgba(76, 175, 80, 0.15)',    color: '#2e7d32' },
  rejected:     { bg: 'rgba(158, 158, 158, 0.15)',  color: '#616161' },
  withdrawn:    { bg: 'rgba(158, 158, 158, 0.15)',  color: '#616161' },
  drafting:        { bg: 'rgba(33, 150, 243, 0.12)', color: '#1565c0' },
  internal_review: { bg: 'rgba(255, 152, 0, 0.15)',  color: '#e65100' },
  revision:        { bg: 'rgba(255, 152, 0, 0.15)',  color: '#e65100' },
  accepted:        { bg: 'rgba(76, 175, 80, 0.15)',  color: '#2e7d32' },
  published:       { bg: 'rgba(76, 175, 80, 0.15)',  color: '#2e7d32' },
};

// Consortium types (primary collaboration category); colors match the create dialog
const CONSORTIUM_TYPES = {
  funding:     { label: 'Funding',     color: '#1976d2', icon: FundingTypeIcon },
  publication: { label: 'Publication', color: '#2e7d32', icon: PublicationTypeIcon },
  // research:    { label: 'Research',    color: '#ed6c02', icon: ResearchTypeIcon },
  // teaching:    { label: 'Teaching',    color: '#0288d1', icon: TeachingTypeIcon },
};

const consortiumCategory = (c) => c.collaboration_summary?.category || 'research';

const ConsortiumTypeChip = ({ consortium, excludeLabel }) => {
  const category = consortiumCategory(consortium);
  const type = CONSORTIUM_TYPES[category];
  const Icon = type?.icon;

  return excludeLabel ? (
    Icon ? (
      <Icon
        sx={{
          fontSize: 16,
          color: `${type.color} !important`,
        }}
      />
    ) : null
  ) : (
    <Chip
      size="small"
      variant="outlined"
      icon={
        Icon ? (
          <Icon
            sx={{
              fontSize: 16,
              color: `${type.color} !important`,
            }}
          />
        ) : undefined
      }
      label={type?.label}
      sx={{
        borderColor: type?.color,
        color: type?.color,
        fontWeight: 600,
      }}
    />
  );
};

export const ProposalStatusChip = ({ proposalStatus }) => {
  if (!proposalStatus?.status) return null;
  const s = proposalStatus.status;
  const colors = PROPOSAL_STATUS_COLORS[s] || { bg: 'rgba(158,158,158,0.12)', color: '#616161' };
  return (
    <Chip
      label={proposalStatus.status_display || s.replace('_', ' ')}
      size="small"
      sx={{ ml: 1, fontWeight: 600, bgcolor: colors.bg, color: colors.color }}
    />
  );
};

const trunc = (s, max = 30) => s && s.length > max ? s.slice(0, max).trimEnd() + '…' : s;

const CallInfoLine = ({ proposalStatus }) => {
  if (!proposalStatus) return null;
  const { agency, program } = proposalStatus;
  if (!agency && !program) return null;
  const parts = agency === program
    ? [trunc(agency)]
    : [program && trunc(program), agency && trunc(agency)].filter(Boolean);
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.3 }}>
      {parts.join(' · ')}
    </Typography>
  );
};

const ConsortiumCard = ({ consortium, onJoin, onView, onToggleVisibility }) => {
  const { user } = useUser();
  const theme = useTheme();
  // Check if user can edit (is coordinator)
  const canEdit = user && consortium.coordinator_member === user.id;

  // Check if user can join (not already a member and consortium is forming)

  const isNotAMember = !ensureArray(consortium.memberships).some(membership =>
    membership.member?.id === user?.id
  );
  const isMember = user && (canEdit || !isNotAMember);

  const handleVisibilityClick = (e) => {
    e.stopPropagation();
    if (!canEdit || !onToggleVisibility) return;
    onToggleVisibility(consortium);
  };

  return (
    <StyledCard onClick={() => onView(consortium.public_id || consortium.id)} 
          sx={{ cursor: 'pointer',
            maxWidth: {xs:'100%', sm:'400px'}
           }}>
      <CardContent sx={{ flexGrow: 1,   }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
          
            <Typography variant="h4"  sx={{ fontWeight: 500, mb: 0.25, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <ConsortiumTypeChip consortium={consortium} excludeLabel={true} /> {consortium.name}
              <ProposalStatusChip proposalStatus={consortium.proposal_status} />
            </Typography>
            {/* <ConsortiumTypeChip consortium={consortium} excludeLabel={true} /> */}
            {/* {consortium.display_title && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {consortium.display_title}
              </Typography>
            )} */}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {isMember && (
              canEdit ? (
                <Tooltip title={consortium.is_public ? 'Public consortium — click to change visibility' : 'Hidden consortium — click to change visibility'}>
                  <IconButton
                    size="small"
                    onClick={handleVisibilityClick}
                    color={consortium.is_public ? 'default' : 'warning'}
                    aria-label={consortium.is_public ? 'Change consortium visibility (currently public)' : 'Change consortium visibility (currently hidden)'}
                  >
                    {consortium.is_public ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={consortium.is_public ? 'Public consortium' : 'Hidden consortium'}>
                  <Box display="flex" alignItems="center" color={consortium.is_public ? 'action.active' : 'warning.main'}>
                    {consortium.is_public ? <VisibilityIcon fontSize="small" color="action" /> : <VisibilityOffIcon fontSize="small" color="warning" />}
                  </Box>
                </Tooltip>
              )
            )}
            <Tooltip title="Members">
              <Badge badgeContent={consortium.member_count || 0} color="primary">
                <GroupIcon />
              </Badge>
            </Tooltip>

         <Box display="flex" gap={0}>
            {/* <IconButton size="small" color="default">
              <StarBorderIcon />
            </IconButton> */}
            {/* <IconButton size="small" color="default"> */}
            <ShareButton />
            {/* </IconButton> */}
          </Box>

            {/* <IconButton size="small">
              <MoreVertIcon />
            </IconButton> */}
          </Box>
        </Box>


        {/* Description */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2}}>
        
        
        
        </Typography>

        {/* Key Details */}
        <Box mt={2}>
          {/* {consortium.call_title && (
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <CampaignIcon fontSize="small" color="action" />
              <Typography variant="caption">
                {consortium.call_title}
              </Typography>
            </Box>
          )} */}
          
          {(consortium.external_coordinator || consortium.coordinator_org_name) && (
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <BusinessIcon fontSize="small" color="action" />
              <Typography variant="caption" lineHeight={1.5}>
                Led by {consortium?.external_coordinator?.org_name
                  ? consortium.external_coordinator.org_name
                  : consortium.coordinator_org_name}
              </Typography>
            </Box>
          )}

          <CallInfoLine proposalStatus={consortium.proposal_status} />

          {consortium.duration_months && (
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <ScheduleIcon fontSize="small" color="action" />
              <Typography variant="caption">
                {consortium.duration_months} months
              </Typography>
            </Box>
          )}

          {(consortium.total_budget_requested || consortium.total_budget_own) && (
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <EuroIcon fontSize="small" color="action" />
              <Typography variant="caption">
                Budget: €{((consortium.total_budget_requested || 0) + (consortium.total_budget_own || 0)) / 1000000}M
              </Typography>
            </Box>
          )}
        </Box>

        {/* Bottom row: team members (left) + keywords (right) */}
        {(ensureArray(consortium.memberships).length > 0 || (Array.isArray(consortium.keywords) && consortium.keywords.length > 0)) && (
          <Box mt={2} display="flex" justifyContent="space-between" alignItems="flex-end" gap={2}>
            {/* Team members */}
            {ensureArray(consortium.memberships).length > 0 && (() => {
              const sortedMemberships = [...ensureArray(consortium.memberships)].sort((a, b) => {
                const rankA = (a.roles || []).reduce((best, r) => Math.max(best, ROLE_RANK[r] ?? -1), -1);
                const rankB = (b.roles || []).reduce((best, r) => Math.max(best, ROLE_RANK[r] ?? -1), -1);
                return rankB - rankA;
              });
              return (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>
                    Team Members:
                  </Typography>
                  <AvatarGroup
                    max={6}
                    spacing="small"
                    sx={{ width: 'fit-content' }}
                    renderSurplus={(surplus) => `+${surplus}`}
                    componentsProps={{
                      additionalAvatar: {
                        sx: {
                          width: 48, height: 48,
                          bgcolor: 'transparent',
                          color: 'primary.light',
                          fontSize: '1.45rem',
                          fontWeight: 500,
                          '&&': {
                            // border: `2px solid ${theme.palette.divider}`,
                            // boxShadow: '0 0 0 5px rgba(0,0,0,0.08)',
                            
                          },
                        },
                      },
                    }}
                  >
                    {sortedMemberships.map((membership, index) => (
                      <Tooltip
                        key={index}
                        title={membership.member?.name || membership.organization?.name || 'Team Member'}
                      >
                        <Avatar
                          src={membership.member?.avatar || undefined}
                          sx={{ width: 32, height: 32, backgroundColor: theme.palette.primary.avatar, color: 'white' }}
                        >
                          {(membership.member?.name || membership.organization?.name || 'M')[0]}
                        </Avatar>
                      </Tooltip>
                    ))}
                  </AvatarGroup>
                </Box>
              );
            })()}

            {/* Keywords: borderless tinted pills */}
            {Array.isArray(consortium.keywords) && consortium.keywords.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end', flexShrink: 1, minWidth: 0 }}>
                {consortium.keywords.slice(0, 4).map((kw) => (
                  <Typography
                    key={kw}
                    component="span"
                    variant="caption"
                    sx={{
                      bgcolor: 'rgba(99,102,241,0.08)',
                      color: 'text.secondary',
                      px: 0.9,
                      py: 0.3,
                      borderRadius: '6px',
                      lineHeight: 1.6,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {kw}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}
      </CardContent>

      {/* Actions */}
      {/* <CardActions sx={{ px: 2, pb: 2 }}>
        <Box display="flex" justifyContent="space-between" width="100%">
          <Box display="flex" gap={1}>
            {/* <Button
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => onView(consortium.public_id || consortium.id)}
              variant="outlined"
            >
              View
            </Button> */}
            {/* {canJoin && (
              <Button
                size="small"
                startIcon={<GroupIcon />}
                onClick={() => onJoin(consortium.public_id || consortium.id)}
                variant="outlined"
                color="primary"
              >
                Join
              </Button>
            )} 

          </Box>
 
        </Box>
      </CardActions> */}
    </StyledCard>
  );
};


const EnhancedConsortiumList = () => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  // ?filter=my opens My Consortia (the feed's profile card links here)
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState(() => (['my', 'available'].includes(searchParams.get('filter')) ? searchParams.get('filter') : 'all'));
  const [typeFilter, setTypeFilter] = useState('all');
  const [onlyPublic, setOnlyPublic] = useState(false);   // the public consortia I am in
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;
  const  { user } = useUser();
  const navigate = useNavigate();
  const theme = useTheme();

  // Check authentication status
  const isAuthenticated = !!user;

  // Debug authentication (only show in development)
  // useEffect(() => {
  //   if (process.env.NODE_ENV === 'development') {
  //     console.log('Auth Debug:', {
  //       isAuthenticated,
  //       hasToken: !!authToken,
  //       user: user?.username || 'none',
  //       tokenPreview: authToken ? `${authToken.substring(0, 20)}...` : 'none'
  //     });
  //   }
  // }, [isAuthenticated, authToken, user]);

  // Fetch user's consortiums
  const { data: userConsortiums = [], error: userError, isLoading: userLoading, mutate: mutateUser } = useSWR(
    user ? '/consortium/consortiums/by-user/' : null,
    fetcher,
    { fallbackData: [] }
  );

  // Fetch available funding calls
  const { data: callsData = null, error: callsError } = useSWR(
    user ? '/funding/calls/' : null,
    fetcher
  );

  const availableCalls = Array.isArray(callsData) ? callsData : (callsData?.results ?? []);

  // Fetch all consortiums for discovery; include server-side search when query is set
  const allConsortiumsKey = searchQuery.trim()
    ? `/consortium/consortiums/?search=${encodeURIComponent(searchQuery.trim())}`
    : '/consortium/consortiums/';
  const { data: allConsortiums = [], error: allError, isLoading: allLoading, mutate: mutateAll } = useSWR(
    allConsortiumsKey,
    fetcher,
    {
      revalidateOnMount: true,
      keepPreviousData: true,
    }
  );

  // Fetch forming consortiums for opportunities
  const { data: formingConsortiums = [], error: formingError, isLoading: formingLoading } = useSWR(
    '/consortium/consortiums/forming/',
    fetcher
  );

  // Fetch active consortiums
  const { data: activeConsortiums = [], error: activeError, isLoading: activeLoading } = useSWR(
    '/consortium/consortiums/active/',
    fetcher
  );

  const loading = userLoading || allLoading || formingLoading || activeLoading;
  const apiErrors = [userError, allError, formingError, activeError, callsError].filter(Boolean);
  const hasApiError = apiErrors.length > 0;

  // Filter consortiums based on the selected filter
  const getFilteredConsortiums = () => {
    // Ensure all data sources are arrays
    const safeUserConsortiums = Array.isArray(userConsortiums) ? userConsortiums : [];
    const safeAllConsortiums = Array.isArray(allConsortiums) ? allConsortiums : [];
    const safeFormingConsortiums = Array.isArray(formingConsortiums) ? formingConsortiums : [];
    const safeActiveConsortiums = Array.isArray(activeConsortiums) ? activeConsortiums : [];

    switch (filter) {
      case 'my':
        return safeUserConsortiums;
      case 'available':
        // Show forming consortiums that user is not already part of
        return safeFormingConsortiums.filter(consortium => 
          !safeUserConsortiums.some(userCons => userCons.id === consortium.id)
        );
      case 'forming':
        return safeFormingConsortiums;
      case 'active':
        return safeActiveConsortiums;
      case 'all':
      default:
        // Merge all unique consortiums from my, available, forming, and active
        const availableConsortiums = safeFormingConsortiums.filter(consortium => 
          !safeUserConsortiums.some(userCons => userCons.id === consortium.id)
        );
        
        // Combine all sources
        const allSources = [
          ...safeUserConsortiums,
          ...availableConsortiums,
          ...safeFormingConsortiums,
          ...safeActiveConsortiums,
          ...safeAllConsortiums
        ];
        
        // Remove duplicates by ID using a Map
        const uniqueConsortiums = Array.from(
          new Map(allSources.map(consortium => [consortium.id, consortium])).values()
        );
        
        return uniqueConsortiums;
    }
  };

  const myConsortiumIds = new Set((Array.isArray(userConsortiums) ? userConsortiums : []).map((c) => c.id));
  const filteredConsortiums = getFilteredConsortiums().filter(
    (c) => (typeFilter === 'all' || consortiumCategory(c) === typeFilter)
      && (!onlyPublic || (c.is_public && myConsortiumIds.has(c.id)))
  );

  // Extra safety check to ensure filteredConsortiums is always an array
  const safeFilteredConsortiums = Array.isArray(filteredConsortiums) ? filteredConsortiums : [];

  // Client-side search across all available list fields (server-side search covers calls/proposals)
  const q = searchQuery.trim().toLowerCase();
  const searchedConsortiums = q
    ? safeFilteredConsortiums.filter((c) => {
        if (c.name?.toLowerCase().includes(q)) return true;
        if (c.title?.toLowerCase().includes(q)) return true;
        if (c.coordinator_org_name?.toLowerCase().includes(q)) return true;
        if (c.coordinator_member_name?.toLowerCase().includes(q)) return true;
        if (c.summary?.toLowerCase().includes(q)) return true;
        if (Array.isArray(c.keywords) && c.keywords.some((kw) => kw.toLowerCase().includes(q))) return true;
        if (Array.isArray(c.memberships) && c.memberships.some(
          (m) =>
            m.organization?.name?.toLowerCase().includes(q) ||
            m.member?.name?.toLowerCase().includes(q)
        )) return true;
        return false;
      })
    : safeFilteredConsortiums;

  // Pagination
  const totalPages = Math.ceil(searchedConsortiums.length / PAGE_SIZE);
  const paginatedConsortiums = searchedConsortiums.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to page 1 when filter or search changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setPage(1), [filter, typeFilter, onlyPublic, searchQuery]);

  // Handle consortium actions

  // Join is not as simple. There must be a join request through an invitation.

  // const handleJoinConsortium = async (consortiumId) => {
  //   try {
  //     await axiosService.post(`/consortium/consortiums/${consortiumId}/join/`);
  //     setSnackbar({ open: true, message: 'Successfully joined consortium!', severity: 'success' });
  //     mutateUser(); // Refresh user's consortiums
  //     mutateAll();  // Refresh all consortiums
  //   } catch (error) {
  //     console.error('Error joining consortium:', error);
  //     setSnackbar({ open: true, message: 'Failed to join consortium', severity: 'error' });
  //   }
  // };

  const handleViewConsortium = (consortiumId) => {
    navigate(`/consortium/${consortiumId}/`);
  };

  // Visibility toggle: a lightweight confirmation dialog that persists via the same
  // PATCH /consortium/consortiums/{id}/settings/ call EditConsortiumDialog uses to save is_public.
  const [visibilityConfirmConsortium, setVisibilityConfirmConsortium] = useState(null);
  const [visibilitySubmitting, setVisibilitySubmitting] = useState(false);

  const handleConfirmVisibilityToggle = async () => {
    const consortium = visibilityConfirmConsortium;
    if (!consortium) return;
    setVisibilitySubmitting(true);
    try {
      await axiosService.patch(
        `/consortium/consortiums/${consortium.id}/settings/`,
        { is_public: !consortium.is_public }
      );
      setSnackbar({
        open: true,
        message: consortium.is_public ? 'Consortium is now hidden' : 'Consortium is now public',
        severity: 'success',
      });
      mutateUser();
      mutateAll();
      setVisibilityConfirmConsortium(null);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to update visibility',
        severity: 'error',
      });
    } finally {
      setVisibilitySubmitting(false);
    }
  };

  const handleCreateConsortium = async (data) => {
    try {
      // Debug log to see what we're working with
      // console.log('=== CREATE COLLABORATION WITH CONSORTIUM DEBUG ===');
      // console.log('data:', JSON.stringify(data, null, 2));
      // console.log('user:', JSON.stringify(user, null, 2));
      
      if (!user?.id) {
        setSnackbar({ open: true, message: 'User not authenticated', severity: 'error' });
        return;
      }

      // Get organization from user object
      const organizationId = user.organization.id;
      
      if (!organizationId) {
        setSnackbar({ open: true, message: 'Please set your organization in your profile first', severity: 'error' });
        return;
      }

      // Extract collaboration data from the nested structure sent by the dialog
      const { collaboration, typeSpecific, collaborationType } = data;

      // Create collaboration with consortium_name - backend will auto-create consortium
      // and establish coordinator membership for the current user
      const collaborationPayload = {
        consortium_name: collaboration.consortium_name,
        consortium_is_public: collaboration.consortium_is_public !== false,
        title: collaboration.title,
        category: collaborationType,
        abstract: collaboration.abstract,
        status: collaboration.status || 'planning',
        start_date: collaboration.start_date || null,
        end_date: collaboration.end_date || null,
        keywords: collaboration.keywords || [],
        lead_organization: organizationId,
        created_by: user.id,
      };

      // Add call if provided (for funding collaborations)
      if (collaboration.call) {
        collaborationPayload.call = collaboration.call;
      }

      // Add type-specific fields based on collaboration type
      if (typeSpecific && Object.keys(typeSpecific).length > 0) {
        // console.log('Adding type-specific fields:', typeSpecific);
        
        // Map type-specific fields based on collaboration type
        switch (collaborationType) {
          case 'publication':
            if (typeSpecific.publication_type) {
              collaborationPayload.publication_type = typeSpecific.publication_type;
            }
            if (typeSpecific.journal_name) {
              collaborationPayload.journal_name = typeSpecific.journal_name;
            }
            if (typeSpecific.publication_date) {
              collaborationPayload.publication_date = typeSpecific.publication_date;
            }
            break;
            
          case 'research':
            if (typeSpecific.research_type) {
              collaborationPayload.research_type = typeSpecific.research_type;
            }
            if (typeSpecific.research_area) {
              collaborationPayload.research_area = typeSpecific.research_area;
            }
            if (typeSpecific.methodology) {
              collaborationPayload.methodology = typeSpecific.methodology;
            }
            if (typeSpecific.funding_amount) {
              collaborationPayload.funding_amount = typeSpecific.funding_amount;
            }
            break;
            
          case 'innovation':
            if (typeSpecific.innovation_type) {
              collaborationPayload.innovation_type = typeSpecific.innovation_type;
            }
            if (typeSpecific.product_name) {
              collaborationPayload.product_name = typeSpecific.product_name;
            }
            if (typeSpecific.technology_readiness_level !== undefined) {
              collaborationPayload.technology_readiness_level = typeSpecific.technology_readiness_level;
            }
            break;
            
          case 'teaching':
            if (typeSpecific.teaching_type) {
              collaborationPayload.teaching_type = typeSpecific.teaching_type;
            }
            if (typeSpecific.target_audience) {
              collaborationPayload.target_audience = typeSpecific.target_audience;
            }
            if (typeSpecific.credits !== undefined) {
              collaborationPayload.credits = typeSpecific.credits;
            }
            break;
            
          case 'outreach':
            if (typeSpecific.outreach_type) {
              collaborationPayload.outreach_type = typeSpecific.outreach_type;
            }
            if (typeSpecific.target_audience) {
              collaborationPayload.target_audience = typeSpecific.target_audience;
            }
            if (typeSpecific.geographic_scope) {
              collaborationPayload.geographic_scope = typeSpecific.geographic_scope;
            }
            break;
            
          case 'distribution':
            if (typeSpecific.distribution_type) {
              collaborationPayload.distribution_type = typeSpecific.distribution_type;
            }
            if (typeSpecific.distribution_model) {
              collaborationPayload.distribution_model = typeSpecific.distribution_model;
            }
            if (typeSpecific.geographic_coverage) {
              collaborationPayload.geographic_coverage = typeSpecific.geographic_coverage;
            }
            break;
            
          case 'funding':
            if (typeSpecific.requested_amount) {
              collaborationPayload.requested_amount = typeSpecific.requested_amount;
            }
            if (typeSpecific.currency) {
              collaborationPayload.currency = typeSpecific.currency;
            }
            if (typeSpecific.proposal_reference) {
              collaborationPayload.proposal_reference = typeSpecific.proposal_reference;
            }
            break;
            
          default:
            // For any unknown collaboration types, add all type-specific fields
            Object.assign(collaborationPayload, typeSpecific);
            break;
        }
      }

      // console.log('Creating collaboration with consortium. Payload:', JSON.stringify(collaborationPayload, null, 2));

      const response = await axiosService.post('/consortium/collaborations/', collaborationPayload);

      // Send invitations (registered users + email-only) to the new consortium. The response names it
      // consortium_id (reading "consortium" found nothing, and every invitation was silently skipped)
      const consortiumId = response.data?.consortium_id ?? response.data?.consortium?.id ?? response.data?.consortium;
      const { invitedMembers = [], emailInvitees = [] } = data;
      const wanted = invitedMembers.length + emailInvitees.length;
      let sent = 0;
      const failed = [];
      if (consortiumId && wanted) {
        const results = await Promise.allSettled([
          ...invitedMembers.map(userId =>
            axiosService.post('/interact/consortium-invitations/', { recipient: userId, consortium: consortiumId })),
          ...emailInvitees.map(email =>
            axiosService.post('/interact/consortium-invitations/', { to_email: email, consortium: consortiumId })),
        ]);
        results.forEach((r) => {
          if (r.status === 'fulfilled') sent += 1;
          else failed.push(apiErrorMessage(r.reason, 'not sent'));
        });
      } else if (wanted) {
        failed.push('the new consortium could not be found');
      }

      setSnackbar({
        open: true,
        message: `Consortium created.${wanted ? ` ${sent} of ${wanted} invitation${wanted === 1 ? '' : 's'} sent.` : ''}`
          + (failed.length ? ` Not sent: ${[...new Set(failed)].join('; ')}. You can invite from the consortium page.` : ''),
        severity: failed.length ? 'warning' : 'success'
      });
      setCreateDialogOpen(false);
      mutateUser(); // Refresh user's consortiums (by-user endpoint)
      mutateAll();  // Refresh all consortiums
      globalMutate(key => typeof key === 'string' && key.includes('/consortium/consortiums/')); // bust useUserConsortia SWR cache (Sidebar + AppBar)
      window.dispatchEvent(new CustomEvent('consortiumCreated')); // notify AppBar chip + menu
    } catch (error) {
      console.error('Error creating collaboration:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.detail 
        || error.response?.data?.message 
        || JSON.stringify(error.response?.data)
        || 'Failed to create collaborative project';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  // Only verified researchers start consortia (the backend refuses everyone else): others are told how
  // to get verified, and that they can still join a consortium they are invited to
  const canStartConsortium = Boolean(user?.is_verified);
  const [verifyInfoOpen, setVerifyInfoOpen] = useState(false);
  const handleOpenCreateDialog = () => {
    if (canStartConsortium) setCreateDialogOpen(true);
    else setVerifyInfoOpen(true);
  };

  if (hasApiError && safeFilteredConsortiums.length === 0) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Unable to Load Consortiums
          </Typography>
          
          {!isAuthenticated ? (
            <>
              <Typography variant="body2" paragraph>
                You are not logged in. Please authenticate to access consortium data.
              </Typography>
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => navigate('/login/')}
                sx={{ mt: 1, mr: 1 }}
              >
                Go to Login
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body2" paragraph>
                Could not load consortium data from the server. This may be a temporary issue.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {apiErrors[0]?.response?.status === 403 ? 'You may not have permission to view this data.'
                  : apiErrors[0]?.response?.status >= 500 ? 'The server encountered an error. Please try again later.'
                  : apiErrors[0]?.message || 'An unexpected error occurred.'}
              </Typography>
              <Button 
                variant="outlined" 
                onClick={() => { mutateUser(); mutateAll(); }}
                sx={{ mt: 1 }}
              >
                Retry
              </Button>
            </>
          )}
        </Alert>
      </Container>
    );
  }



  return (
    <PageContainer>
      {/* API Warning Banner */}
      {hasApiError && safeFilteredConsortiums.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }} variant="outlined">
          <Typography variant="body2">
            Some data may be incomplete.
            {!isAuthenticated 
              ? ' Please log in to access full consortium data.'
              : ` Failed to load some resources (${apiErrors.map(e => e?.response?.status || 'network error').join(', ')}). Data shown may be partial.`
            }
          </Typography>
        </Alert>
      )}

      {/* Header: the list's views as tabs after the title, search and "create" at the right (the Hub's header) */}
      <PageHeader title="Consortium List"
        subtitle="Join collaborative research networks and build partnerships for funding applications"
        actions={(
          <>
            <TextField
              size="small"
              placeholder="Search consortia, people, calls…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 300, mr:1 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Fab
              size="medium"
              color="primary"
              onClick={handleOpenCreateDialog}
              aria-label={canStartConsortium ? 'Start a consortium' : 'Starting a consortium needs a verified organisation membership'}
              sx={{
                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                color: '#1976d2',
                '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.2)' },
              }}
            >
              <AddIcon />
            </Fab>
          </>
        )}>
        <Tabs value={filter} onChange={(_e, v) => setFilter(v)} variant="scrollable" scrollButtons="auto"
          sx={{ ...HEADER_TABS_SX, minWidth: 0 }}>
          <Tab value="all" label="All" />
          <Tab value="my" label="My Consortia" />
          <Tab value="available" label="Available to Join" />
        </Tabs>
      </PageHeader>

      {/* Consortium types */}
      <Box sx={{ px: 2, py: 1.25, mt: 1.5, mb:.5}}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {/* <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Type:</Typography> */}
          <Chip
            size="small"
            label="All types"
            onClick={() => setTypeFilter('all')}
            variant= 'outlined'
            color= 'default'
            sx={{
              ...(typeFilter === 'all' && {
                bgcolor: theme.palette.primary.ulight,
                borderColor: theme.palette.primary.main,
                
                
              }),
            }}
          />
          {Object.entries(CONSORTIUM_TYPES).map(([key, type]) => {
            const Icon = type.icon;
            const selected = typeFilter === key;
            return (
              <Chip
                key={key}
                size="small"
                icon={<Icon sx={{ fontSize: 16, color: `${selected ? '#fff' : type.color} !important` }} />}
                label={type.label}
                onClick={() => setTypeFilter(key)}
                variant={selected ? 'filled' : 'outlined'}
                sx={selected ? { bgcolor: type.color, color: '#fff', '&:hover': { bgcolor: type.color } } : { borderColor: type.color, color: type.color }}
              />
            );
          })}
          {filter !== 'available' && (
            // a switch, not another chip: the chips on this row are the consortium types
            <Tooltip title={onlyPublic ? 'Show all consortia' : 'Show only the public consortia you are in'}>
              <FormControlLabel sx={{ m: 0, ml: { sm: 'auto' }, whiteSpace: 'nowrap' }}
                control={<Switch size="small" checked={onlyPublic} onChange={(e) => setOnlyPublic(e.target.checked)} />}
                label={<Typography variant="body2" color="text.secondary">Only public</Typography>} />
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Loading State — skeleton cards */}
      {loading && (
        <Grid container spacing={3}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Grid item xs={12} md={6} lg={4} key={i}>
              <Card sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box flex={1}>
                      <Skeleton variant="text" width="70%" height={28} />
                      <Skeleton variant="text" width="40%" height={18} />
                    </Box>
                  </Box>
                  <Skeleton variant="text" width="100%" />
                  <Skeleton variant="text" width="90%" />
                  <Skeleton variant="text" width="60%" sx={{ mb: 2 }} />
                  <Box display="flex" gap={1} mb={2}>
                    <Skeleton variant="rounded" width={64} height={24} />
                    <Skeleton variant="rounded" width={80} height={24} />
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex">
                      {[0, 1, 2].map((j) => (
                        <Skeleton key={j} variant="circular" width={28} height={28} sx={{ ml: j > 0 ? -0.8 : 0 }} />
                      ))}
                    </Box>
                    <Skeleton variant="rounded" width={72} height={32} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Consortiums Grid */}
      {!loading && paginatedConsortiums.length > 0 && (
        <Grid container spacing={3}>
          {paginatedConsortiums.map((consortium) => (
            <Grid item xs={12} md={6} lg={4} key={consortium.id}>
              <ConsortiumCard
                consortium={consortium}
                // onJoin={handleJoinConsortium}
                onView={handleViewConsortium}
                onToggleVisibility={setVisibilityConfirmConsortium}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            color="primary"
          />
        </Box>
      )}

      {!loading && searchedConsortiums.length === 0 && (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center"
          sx={{ mt: 8, textAlign: 'center' }}
        >
          <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No consortiums found
          </Typography>
          {filter !== 'available' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Start building your research network by creating or joining a consortium
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateDialog}
              >
                Create First Consortium
              </Button>
            </>
          )}
        </Box>
      )}

      <Dialog open={verifyInfoOpen} onClose={() => setVerifyInfoOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Verification needed</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Only verified researchers can start a consortium. Add your organisation to your profile and ask for your
            membership to be verified.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can still join any consortium you are invited to: accept the invitation in your notifications or on
            the consortium&apos;s page.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyInfoOpen(false)}>Close</Button>
          <Button variant="contained" onClick={() => { setVerifyInfoOpen(false); navigate(`/profile/${user?.id}/`); }}>
            Go to my profile
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Create Dialog */}
      <EnhancedCreateConsortiumDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreate={handleCreateConsortium}
        availableCalls={availableCalls}
      />

      {/* Visibility confirmation — persists via the same settings PATCH call EditConsortiumDialog uses */}
      <Dialog
        open={!!visibilityConfirmConsortium}
        onClose={() => !visibilitySubmitting && setVisibilityConfirmConsortium(null)}
      >
        <DialogTitle>
          {visibilityConfirmConsortium?.is_public ? 'Hide this consortium?' : 'Make this consortium public?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {visibilityConfirmConsortium?.is_public
              ? `"${visibilityConfirmConsortium?.name}" will no longer be visible to non-members in the consortium list.`
              : `"${visibilityConfirmConsortium?.name}" will become visible to everyone in the consortium list.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVisibilityConfirmConsortium(null)} disabled={visibilitySubmitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirmVisibilityToggle} disabled={visibilitySubmitting} variant="contained">
            {visibilityConfirmConsortium?.is_public ? 'Hide' : 'Make Public'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default EnhancedConsortiumList;